import { useRef, useState, type DragEvent } from 'react';
import { PlusIcon, Square2StackIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { userAppContext } from '@/context/Context';
import type { UserAppContextType } from '@/router/types';
import { useSearchTabsBar } from '@/store/searchTabsStore';
import { useSearchTabNavigation } from '@/hooks/search/useSearchTabNavigation';
import { isSearchTabsEnabled } from '@/config/searchTabs';
import { buildAllResultsSearch, resolveSearchSource } from '@/utils/allResultsSearch';
import { isCleanAllResultsSearch } from '@/utils/searchTabIdentity';
import { useSearchSpotlightOptional } from '@/modules/search/spotlight';
import './SearchBrowserTabs.css';

export function SearchBrowserTabs() {
  const { tabs, activeTabId, canOpenTab, maxTabs } = useSearchTabsBar();
  const {
    switchToTab,
    closeSearchTab,
    closeAllSearchTabs,
    duplicateSearchTab,
    reorderSearchTabs,
    navigateSearch,
  } = useSearchTabNavigation();
  const spotlight = useSearchSpotlightOptional();
  const { restrictSources, groups } = userAppContext() as unknown as UserAppContextType;
  const dragFromIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const source = resolveSearchSource({ restrictSources, groups });
  const blankSearch = () => buildAllResultsSearch(source);
  const onlyCleanAllResults = tabs.length === 1 && isCleanAllResultsSearch(tabs[0]?.search ?? '');

  if (!isSearchTabsEnabled || tabs.length === 0) {
    return null;
  }

  const handleOpenBlank = () => {
    if (onlyCleanAllResults && spotlight) {
      spotlight.open();
      return;
    }
    if (!canOpenTab) return;
    const blank = blankSearch();
    navigateSearch({
      search: blank.search,
      title: blank.title,
      openInNewTab: true,
    });
  };

  const handleCloseAll = () => {
    if (tabs.length <= 1) return;
    closeAllSearchTabs(blankSearch);
  };

  const handleDragStart = (index: number) => (evt: DragEvent) => {
    const target = evt.target as HTMLElement;
    if (target.closest('button.search-browser-tabs__action, button.search-browser-tabs__close')) {
      evt.preventDefault();
      return;
    }
    dragFromIndexRef.current = index;
    evt.dataTransfer.effectAllowed = 'move';
    evt.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index: number) => (evt: DragEvent) => {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (toIndex: number) => (evt: DragEvent) => {
    evt.preventDefault();
    const fromIndex = dragFromIndexRef.current;
    dragFromIndexRef.current = null;
    setDragOverIndex(null);
    if (fromIndex == null || fromIndex === toIndex) return;
    reorderSearchTabs(fromIndex, toIndex);
  };

  const handleDragEnd = () => {
    dragFromIndexRef.current = null;
    setDragOverIndex(null);
  };

  const addTooltip = onlyCleanAllResults
    ? 'Search and open in a new tab'
    : canOpenTab
      ? 'New search tab'
      : `Maximum of ${maxTabs} tabs reached`;

  return (
    <div className="search-browser-tabs" data-testid="search-browser-tabs">
      <div className="search-browser-tabs__list" role="tablist" aria-label="Search tabs">
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTabId;
          const isDropTarget = dragOverIndex === index;
          return (
            <div
              key={tab.id}
              className={`search-browser-tabs__tab ${isActive ? 'is-active' : ''}${isDropTarget ? ' is-drop-target' : ''}`}
              role="presentation"
              draggable
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              <span className="search-browser-tabs__dot" aria-hidden />
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                id={`search-tab-${tab.id}`}
                className="search-browser-tabs__tab-button"
                title={tab.title}
                onClick={() => {
                  if (!isActive) switchToTab(tab.id);
                }}
              >
                <span className="search-browser-tabs__tab-title">{tab.title}</span>
              </button>
              <BaseTooltip
                content={canOpenTab ? 'Duplicate tab' : `Maximum of ${maxTabs} tabs reached`}
                position="bottom"
              >
                <button
                  type="button"
                  className="search-browser-tabs__action"
                  aria-label={`Duplicate tab ${tab.title}`}
                  disabled={!canOpenTab}
                  onClick={evt => {
                    evt.stopPropagation();
                    duplicateSearchTab(tab.id);
                  }}
                >
                  <Square2StackIcon className="h-3 w-3" aria-hidden />
                </button>
              </BaseTooltip>
              <button
                type="button"
                className="search-browser-tabs__close"
                aria-label={`Close tab ${tab.title}`}
                title="Close tab"
                onClick={evt => {
                  evt.stopPropagation();
                  closeSearchTab(tab.id, blankSearch);
                }}
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      <div className="search-browser-tabs__meta">
        {tabs.length > 1 && (
          <BaseTooltip content="Close all tabs" position="bottom">
            <button
              type="button"
              className="search-browser-tabs__clear"
              aria-label="Close all tabs"
              onClick={handleCloseAll}
              data-testid="search-browser-tabs-clear"
            >
              <TrashIcon className="h-4 w-4" aria-hidden />
            </button>
          </BaseTooltip>
        )}
        <span className="search-browser-tabs__count" aria-live="polite">
          {tabs.length}/{maxTabs}
        </span>
        <BaseTooltip content={addTooltip} position="bottom">
          <button
            type="button"
            className="search-browser-tabs__add"
            aria-label={onlyCleanAllResults ? 'Open search' : 'Open new search tab'}
            disabled={!onlyCleanAllResults && !canOpenTab}
            onClick={handleOpenBlank}
            data-testid="search-browser-tabs-add"
          >
            <PlusIcon className="h-4 w-4" aria-hidden />
          </button>
        </BaseTooltip>
      </div>
    </div>
  );
}

export default SearchBrowserTabs;
